import React, {Fragment} from "react";
import {connect, useSelector} from "react-redux";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import ExternalLogin from "../components/external-login";
import Home from "../components/home";
import ModulePage from "../components/module-page";
import Node from "../components/node";
import PageNotFound from "../components/page-not-found";
import ServiceUnavailable from "../components/service-unavailable";
import WithCustomTheme from "../components/with-custom-theme";
import A11yDomain from "../domains/A11yDomain";
import AppDomain from "../domains/AppDomain";
import ConfirmEmailDomain from "../domains/ConfirmEmailDomain";
import HubDomain from "../domains/HubDomain";
import ItemContainersDomain from "../domains/ItemContainersDomain";
import MultiLanguageDomain from "../domains/MultiLanguageDomain";
import NodeDomain from "../domains/NodeDomain";
import ResetPasswordDomain from "../domains/ResetPasswordDomain";
import Call from "../hocs/call";
import {ItemContainerTypes} from "../model/item-containers-models/itemContainerType";
import AiConfig from "../pages/AiConfig";
import WmsViewerPage from "../pages/WmsViewerPage";
import NodeRouter from "./NodeRouter";
import {clientBasePathSelector} from "../state/config/configSelectors";
import {hubCustomHomePageIdSelector} from "../state/hub/hubSelectors";
import {nodeCustomHomePageIdSelector} from "../state/node/nodeSelectors";
import {showUserLoginForm} from "../state/user/userActions";

const mapStateToProps = state => ({
  hub: state.hub,
  node: state.node,
  moduleConfigs: state.moduleConfigs,
  user: state.user
});

const mapDispatchToProps = dispatch => ({
  onLoginFormShow: () => dispatch(showUserLoginForm())
});

const HubRouter = ({hub, node, moduleConfigs, user, onLoginFormShow}) => {
  const defaultNode = (hub?.nodes || []).find(node => node.default === true) || null;
  const isDefault = (node && defaultNode && node.nodeId === defaultNode.nodeId) || false;
  const nodeCode = node ? node.code : null;

  const clientBasePath = useSelector(clientBasePathSelector);
  const hubHomePageId = useSelector(hubCustomHomePageIdSelector);
  const defaultNodeHomePageId = useSelector(nodeCustomHomePageIdSelector(defaultNode?.nodeId ?? -1));

  return (
    <BrowserRouter basename={clientBasePath}>
      <AppDomain>
        <WithCustomTheme nodeCode={nodeCode}>
          <A11yDomain>
            <Routes>
              <Route
                path="/"
                element={
                  <HubDomain>
                    {defaultNode !== null ? (
                      <NodeDomain nodeCode={defaultNode.code}>
                        {defaultNodeHomePageId ? (
                          <ItemContainersDomain
                            itemContainerId={defaultNodeHomePageId}
                            hideBreadcrumbs
                            nodeCode={nodeCode}
                            isDefault={isDefault}
                            itemContainerType={ItemContainerTypes.CustomPage}
                          />
                        ) : (
                          <Node nodeCode={defaultNode.code} isDefault />
                        )}
                      </NodeDomain>
                    ) : hubHomePageId ? (
                      <ItemContainersDomain
                        itemContainerId={hubHomePageId}
                        hideBreadcrumbs
                        nodeCode={nodeCode}
                        isDefault={isDefault}
                        itemContainerType={ItemContainerTypes.CustomPage}
                      />
                    ) : (
                      <Home />
                    )}
                  </HubDomain>
                }
              />
              <Route path="/auth/provider" element={<ExternalLogin />} />
              <Route
                path="/:lang"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      {defaultNode !== null ? (
                        <NodeDomain nodeCode={defaultNode.code}>
                          {defaultNodeHomePageId ? (
                            <ItemContainersDomain
                              itemContainerId={defaultNodeHomePageId}
                              hideBreadcrumbs
                              nodeCode={nodeCode}
                              isDefault={isDefault}
                              itemContainerType={ItemContainerTypes.CustomPage}
                            />
                          ) : (
                            <Node nodeCode={defaultNode.code} isDefault />
                          )}
                        </NodeDomain>
                      ) : hubHomePageId ? (
                        <ItemContainersDomain
                          itemContainerId={hubHomePageId}
                          hideBreadcrumbs
                          nodeCode={nodeCode}
                          isDefault={isDefault}
                          itemContainerType={ItemContainerTypes.CustomPage}
                        />
                      ) : (
                        <Home />
                      )}
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
              {moduleConfigs.modules.includes("dashboard") && (
                <Route
                  path="/:lang/dashboards"
                  element={
                    <MultiLanguageDomain>
                      <HubDomain>
                        <ItemContainersDomain itemContainerType={ItemContainerTypes.Dashboard} />
                      </HubDomain>
                    </MultiLanguageDomain>
                  }
                />
              )}
              {moduleConfigs.modules.includes("dashboard") && (
                <Route
                  path="/:lang/dashboards/:id"
                  element={
                    <MultiLanguageDomain>
                      <HubDomain>
                        <ItemContainersDomain itemContainerType={ItemContainerTypes.Dashboard} />
                      </HubDomain>
                    </MultiLanguageDomain>
                  }
                />
              )}
              {moduleConfigs.modules.includes("custom-page") && (
                <Route
                  path="/:lang/pages/:id"
                  element={
                    <MultiLanguageDomain>
                      <HubDomain>
                        <ItemContainersDomain itemContainerType={ItemContainerTypes.CustomPage} />
                      </HubDomain>
                    </MultiLanguageDomain>
                  }
                />
              )}
              <Route
                path="/:lang/resetPassword"
                element={
                  <MultiLanguageDomain>
                    <ResetPasswordDomain>
                      {defaultNode !== null ? (
                        <HubDomain>
                          <NodeDomain nodeCode={defaultNode.code}>
                            <Node nodeCode={defaultNode.code} isDefault />
                          </NodeDomain>
                        </HubDomain>
                      ) : (
                        <HubDomain>
                          <Home />
                        </HubDomain>
                      )}
                    </ResetPasswordDomain>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/confirmEmail"
                element={
                  <MultiLanguageDomain>
                    <ConfirmEmailDomain>
                      <Fragment>
                        {defaultNode !== null ? (
                          <HubDomain>
                            <NodeDomain nodeCode={defaultNode.code}>
                              <Node nodeCode={defaultNode.code} isDefault />
                            </NodeDomain>
                          </HubDomain>
                        ) : (
                          <HubDomain>
                            <Home />
                          </HubDomain>
                        )}
                      </Fragment>
                    </ConfirmEmailDomain>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/auth"
                element={
                  <MultiLanguageDomain>
                    <Call cb={onLoginFormShow} disabled={user.isAuthenticated}>
                      <Fragment>
                        {defaultNode !== null ? (
                          <HubDomain>
                            <NodeDomain nodeCode={defaultNode.code}>
                              <Node nodeCode={defaultNode.code} isDefault />
                            </NodeDomain>
                          </HubDomain>
                        ) : (
                          <HubDomain>
                            <Home />
                          </HubDomain>
                        )}
                      </Fragment>
                    </Call>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/config-ai"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      <AiConfig defaultNode={defaultNode} />
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/service-unavailable"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      <ServiceUnavailable defaultNode={defaultNode} />
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/not-found"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      <PageNotFound defaultNode={defaultNode} />
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
              {(moduleConfigs.hubRoutes || []).map(({id, route, component, fallback, ...props}) => {
                return (
                  <Route
                    key={id}
                    path={`/:lang/${route}/*`}
                    element={
                      <MultiLanguageDomain>
                        <HubDomain>
                          <ModulePage moduleId={id} moduleComponent={component} moduleFallback={fallback} {...props} />
                        </HubDomain>
                      </MultiLanguageDomain>
                    }
                  />
                );
              })}
              <Route
                path="/:lang/:nodeCode/*"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      <NodeDomain>
                        <NodeRouter isDefault={isDefault} />
                      </NodeDomain>
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
              <Route
                path="/:lang/wms"
                element={
                  <MultiLanguageDomain>
                    <HubDomain>
                      <WmsViewerPage />
                    </HubDomain>
                  </MultiLanguageDomain>
                }
              />
            </Routes>
          </A11yDomain>
        </WithCustomTheme>
      </AppDomain>
    </BrowserRouter>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(HubRouter);
